// Performance Admin — seed the 4 data pillars the Leaderboard needs.
// Tabs: Employees · Daily MIS · Task SLA · Sales Targets.
import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { UsersThree, Calendar, ClipboardText, Target as TargetIcon, UploadSimple, Trash, Plus } from "@phosphor-icons/react";

const DEPARTMENTS = ["Sales", "Field Engineering", "Operations", "Procurement"];
const TABS = ["employees", "mis", "tasks", "targets"];

export default function PerformanceAdmin() {
  const [tab, setTab] = useState("employees");
  const [employees, setEmployees] = useState([]);

  const loadEmp = () => api.get("/performance/employees").then(r => setEmployees(r.data || [])).catch(() => {});
  useEffect(() => { loadEmp(); }, []);

  return (
    <Layout title="Performance Admin" subtitle="Seed the 4 pillars: employees · MIS · tasks · targets">
      <div className="mb-6" data-testid="perf-admin-page">
        <div className="flex flex-wrap gap-1 border-b border-black/10">
          {TABS.map(t => (
            <button key={t} data-testid={`perf-tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider font-bold border-b-2 -mb-px transition ${tab === t ? "border-[#7C3AED] text-[#0A0A0A]" : "border-transparent text-[#717680] hover:text-black"}`}>
              {t === "mis" ? "Daily MIS" : t === "tasks" ? "Task SLA" : t === "targets" ? "Sales Targets" : "Employees"}
            </button>
          ))}
        </div>
      </div>

      {tab === "employees" && <EmployeesTab employees={employees} reload={loadEmp} />}
      {tab === "mis"       && <MISTab employees={employees} />}
      {tab === "tasks"     && <TaskSLATab employees={employees} />}
      {tab === "targets"   && <TargetsTab employees={employees} />}
    </Layout>
  );
}

// -------- Employees tab --------
function EmployeesTab({ employees, reload }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dept, setDept] = useState("Sales");
  const [role, setRole] = useState("sales");
  const [csvText, setCsvText] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const fileRef = useRef(null);

  const addOne = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      await api.post("/performance/employees", { name, email, department: dept, role_type: role });
      toast.success(`Added ${name}`); setName(""); setEmail(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const parseCsv = () => {
    const lines = (csvText || "").split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return [];
    const hasHeader = /name|email|department|role/i.test(lines[0]);
    const dataLines = hasHeader ? lines.slice(1) : lines;
    return dataLines.map(l => {
      const [name = "", email = "", department = "Sales", role_type = "sales"] = l.split(",").map(s => s.trim());
      return { name, email, department, role_type };
    }).filter(r => r.name);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      setCsvText(text); setCsvFileName(f.name);
      toast.success(`${f.name} loaded`);
    } catch { toast.error("Read failed"); }
  };

  const doBulk = async () => {
    const rows = parseCsv();
    if (!rows.length) return toast.error("No valid rows");
    try {
      const r = await api.post("/performance/employees/bulk-import", rows);
      toast.success(`Created ${r.data.created} · Skipped ${r.data.skipped_duplicate} dup · ${r.data.error_count} errors`);
      setCsvText(""); setCsvFileName(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="perf-employees-tab">
      <div className="lg:col-span-1 space-y-4">
        <div className="border border-black/10 bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#717680] mb-3">Add single employee</div>
          <input data-testid="pe-name" value={name} onChange={e=>setName(e.target.value)} placeholder="Full name" className="w-full border border-black/15 px-3 py-2 text-sm mb-2" />
          <input data-testid="pe-email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Work email (auto-links to MIS + tasks)" className="w-full border border-black/15 px-3 py-2 text-sm mb-2" />
          <select data-testid="pe-dept" value={dept} onChange={e=>setDept(e.target.value)} className="w-full border border-black/15 px-3 py-2 text-sm mb-2 bg-white">
            {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
          </select>
          <select data-testid="pe-role" value={role} onChange={e=>setRole(e.target.value)} className="w-full border border-black/15 px-3 py-2 text-sm mb-3 bg-white">
            <option value="sales">Sales (bag revenue)</option>
            <option value="non_sales">Non-sales (execute tasks)</option>
          </select>
          <button data-testid="pe-add" onClick={addOne} className="w-full px-4 py-2 text-xs font-bold uppercase tracking-wider bg-black text-white hover:bg-[#7C3AED]"><Plus size={12} weight="bold" className="inline mr-1" />Add employee</button>
        </div>

        <div className="border border-black/10 bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#717680] mb-1">Bulk CSV import</div>
          <div className="text-[10px] text-[#717680] mb-3 italic">Auto-pulls MIS + task data · leave email blank if not needed</div>
          <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} data-testid="pe-csv-file" />
          <button onClick={() => fileRef.current?.click()} data-testid="pe-csv-btn" className="w-full text-xs font-semibold border border-dashed border-black/15 p-3 hover:bg-black hover:text-white transition inline-flex items-center justify-center gap-2">
            <UploadSimple size={14} weight="bold" /> {csvFileName || "Choose CSV"}
          </button>
          <textarea data-testid="pe-csv-input" rows={5} value={csvText} onChange={e=>setCsvText(e.target.value)} placeholder={"name,email,department,role_type\nAlice,alice@shelvestech.com,Sales,sales\nBob,bob@shelvestech.com,Field Engineering,non_sales"} className="w-full border border-black/15 px-3 py-2 text-xs font-mono mt-2" />
          <button data-testid="pe-bulk-submit" onClick={doBulk} className="w-full mt-2 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#7C3AED] text-white hover:bg-black">Import all</button>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#717680] mb-3">{employees.length} active employees</div>
        <div className="border border-black/10 bg-white divide-y divide-black/5">
          {employees.length === 0 && <div className="p-6 text-center text-xs text-[#717680] italic">No employees yet — add one on the left.</div>}
          {employees.map(e => (
            <div key={e.id} className="p-3 flex items-center gap-3" data-testid={`pe-row-${e.id}`}>
              <div className="w-8 h-8 rounded-full bg-[#7C3AED]/15 text-[#7C3AED] flex items-center justify-center text-xs font-bold">{e.name.charAt(0).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{e.name}</div>
                <div className="text-[11px] text-[#717680]">{e.department} · {e.role_type === "sales" ? "Sales" : "Non-sales"}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------- Daily MIS tab --------
function MISTab({ employees }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState({}); // employee_id → "on_time"|"late"|"absent"
  const [busy, setBusy] = useState(false);

  const submitAll = async () => {
    const entries = Object.entries(rows).filter(([, s]) => s);
    if (!entries.length) return toast.error("Mark at least one row");
    setBusy(true);
    let ok = 0, fail = 0;
    for (const [emp_id, status] of entries) {
      try { await api.post("/performance/mis-log", { employee_id: emp_id, date, status }); ok++; }
      catch { fail++; }
    }
    setBusy(false);
    toast.success(`Logged ${ok} entries · ${fail} failed`);
    setRows({});
  };

  return (
    <div data-testid="perf-mis-tab">
      <div className="mb-4 border-l-4 border-[#25D366] bg-[#F0FFF4] px-4 py-3 text-xs" data-testid="mis-auto-banner">
        <b>Auto-sourced.</b> The leaderboard now pulls MIS attendance from your <code>MIS module</code> daily entries automatically. This manual logger is optional — use it only for employees who don't log daily MIS themselves.
      </div>
      <div className="flex items-center gap-3 mb-5">
        <input type="date" data-testid="mis-date" value={date} onChange={e=>setDate(e.target.value)} className="border border-black/15 px-3 py-2 text-sm" />
        <button data-testid="mis-submit" onClick={submitAll} disabled={busy} className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#7C3AED] text-white hover:bg-black disabled:opacity-40">{busy ? "Saving…" : "Save MIS for date"}</button>
      </div>
      <div className="border border-black/10 bg-white divide-y divide-black/5">
        {employees.length === 0 && <div className="p-6 text-center text-xs text-[#717680] italic">No employees yet — add them first.</div>}
        {employees.map(e => (
          <div key={e.id} className="p-3 flex items-center gap-3" data-testid={`mis-row-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{e.name}</div>
              <div className="text-[11px] text-[#717680]">{e.department}</div>
            </div>
            <div className="flex gap-1">
              {[["on_time", "On time", "#25D366"], ["late", "Late", "#F59E0B"], ["absent", "Absent", "#D90429"]].map(([v, l, c]) => (
                <button key={v} data-testid={`mis-${e.id}-${v}`} onClick={() => setRows(r => ({...r, [e.id]: v}))}
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition ${rows[e.id]===v ? "text-white" : "border-black/15 hover:bg-black/5"}`}
                  style={rows[e.id]===v ? { background: c, borderColor: c } : {}}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Task SLA tab --------
function TaskSLATab({ employees }) {
  const [employeeId, setEmployeeId] = useState("");
  const [category, setCategory] = useState("");
  const [dateAssigned, setDateAssigned] = useState(() => new Date().toISOString().slice(0, 10));
  const [targetHrs, setTargetHrs] = useState(24);
  const [actualHrs, setActualHrs] = useState(0);
  const [slaStatus, setSlaStatus] = useState("within_sla");

  const submit = async () => {
    if (!employeeId) return toast.error("Pick employee");
    try {
      await api.post("/performance/task-sla", {
        employee_id: employeeId, task_category: category, date_assigned: dateAssigned,
        target_tat_hours: +targetHrs, actual_closure_hours: +actualHrs, sla_status: slaStatus,
      });
      toast.success("Task logged");
      setCategory(""); setActualHrs(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="max-w-2xl" data-testid="perf-tasks-tab">
      <div className="mb-4 border-l-4 border-[#25D366] bg-[#F0FFF4] px-4 py-3 text-xs" data-testid="tasks-auto-banner">
        <b>Auto-sourced.</b> The leaderboard also pulls task completion data from <code>Shelves-Track</code> (Tracker) using the employee's email. Use this manual logger only for tasks tracked outside the platform.
      </div>
      <div className="border border-black/10 bg-white p-6 space-y-4">
        <select data-testid="task-employee" value={employeeId} onChange={e=>setEmployeeId(e.target.value)} className="w-full border border-black/15 px-3 py-2 text-sm bg-white">
          <option value="">— Pick employee —</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.name} · {e.department}</option>)}
        </select>
        <input data-testid="task-category" value={category} onChange={e=>setCategory(e.target.value)} placeholder="Task category (e.g. Installation, Servicing, Delivery)" className="w-full border border-black/15 px-3 py-2 text-sm" />
        <div className="grid grid-cols-3 gap-2">
          <label className="text-xs">Date <input type="date" data-testid="task-date" value={dateAssigned} onChange={e=>setDateAssigned(e.target.value)} className="w-full border border-black/15 px-2 py-1.5 text-xs mt-1" /></label>
          <label className="text-xs">Target hrs <input type="number" data-testid="task-target-hrs" value={targetHrs} onChange={e=>setTargetHrs(e.target.value)} className="w-full border border-black/15 px-2 py-1.5 text-xs mt-1" /></label>
          <label className="text-xs">Actual hrs <input type="number" data-testid="task-actual-hrs" value={actualHrs} onChange={e=>setActualHrs(e.target.value)} className="w-full border border-black/15 px-2 py-1.5 text-xs mt-1" /></label>
        </div>
        <div className="flex gap-1">
          {[["within_sla", "Within SLA", "#25D366"], ["delayed", "Delayed", "#F59E0B"], ["overdue_pending", "Overdue / Pending", "#D90429"]].map(([v, l, c]) => (
            <button key={v} data-testid={`task-sla-${v}`} onClick={() => setSlaStatus(v)}
              className={`flex-1 px-3 py-2 text-[10px] font-bold uppercase tracking-widest border transition ${slaStatus===v ? "text-white" : "border-black/15 hover:bg-black/5"}`}
              style={slaStatus===v ? { background: c, borderColor: c } : {}}>{l}</button>
          ))}
        </div>
        <button data-testid="task-save" onClick={submit} className="w-full px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#7C3AED] text-white hover:bg-black">Log task</button>
      </div>
      <div className="mt-3 text-[11px] text-[#717680]">Log each closed task with its SLA outcome. TAT % on the leaderboard = tasks within SLA ÷ total tasks.</div>
    </div>
  );
}

// -------- Sales Targets tab --------
function TargetsTab({ employees }) {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState({}); // employee_id → {target, pipeline, achieved}
  const salesReps = useMemo(() => employees.filter(e => e.role_type === "sales"), [employees]);

  const update = (id, field, v) => setRows(r => ({ ...r, [id]: { ...(r[id]||{}), [field]: v }}));

  const submit = async () => {
    const entries = Object.entries(rows).filter(([, v]) => v && (v.target || v.pipeline || v.achieved));
    if (!entries.length) return toast.error("Fill at least one row");
    let ok = 0, fail = 0;
    for (const [emp_id, v] of entries) {
      try {
        await api.post("/performance/sales-target", {
          employee_id: emp_id, period,
          target: +(v.target || 0), pipeline: +(v.pipeline || 0), achieved: +(v.achieved || 0),
        });
        ok++;
      } catch { fail++; }
    }
    toast.success(`Saved ${ok} targets · ${fail} failed`);
    setRows({});
  };

  return (
    <div data-testid="perf-targets-tab">
      <div className="flex items-center gap-3 mb-5">
        <input type="month" data-testid="tgt-period" value={period} onChange={e=>setPeriod(e.target.value)} className="border border-black/15 px-3 py-2 text-sm" />
        <button data-testid="tgt-save" onClick={submit} className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#7C3AED] text-white hover:bg-black">Save all targets</button>
      </div>
      {salesReps.length === 0 && <div className="text-xs text-[#717680] italic p-6 text-center border border-dashed border-black/15">No sales-role employees. Add employees with role type "Sales".</div>}
      <div className="border border-black/10 bg-white divide-y divide-black/5">
        {salesReps.map(e => (
          <div key={e.id} className="p-3 grid grid-cols-4 gap-3 items-center" data-testid={`tgt-row-${e.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{e.name}</div>
              <div className="text-[11px] text-[#717680]">{e.department}</div>
            </div>
            <label className="text-[10px]">Target ₹ <input type="number" data-testid={`tgt-target-${e.id}`} value={rows[e.id]?.target || ""} onChange={ev=>update(e.id, "target", ev.target.value)} className="w-full border border-black/15 px-2 py-1.5 text-xs mt-1" placeholder="500000" /></label>
            <label className="text-[10px]">Pipeline ₹ <input type="number" data-testid={`tgt-pipeline-${e.id}`} value={rows[e.id]?.pipeline || ""} onChange={ev=>update(e.id, "pipeline", ev.target.value)} className="w-full border border-black/15 px-2 py-1.5 text-xs mt-1" placeholder="800000" /></label>
            <label className="text-[10px]">Achieved ₹ <input type="number" data-testid={`tgt-achieved-${e.id}`} value={rows[e.id]?.achieved || ""} onChange={ev=>update(e.id, "achieved", ev.target.value)} className="w-full border border-black/15 px-2 py-1.5 text-xs mt-1" placeholder="450000" /></label>
          </div>
        ))}
      </div>
    </div>
  );
}
